'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Autocomplete,
} from '@mui/material';
import LatePaymentLineGraoh from './LatePaymentLineGraph';
import LatePaymentsPieChart from './LatePaymentsPieChart';
import LatePaymentsBarChart from './LatePaymentsBarChart';
import ProcessingStatusLineChart from './ProcessingStatusLineChart';
import useFetchLatePayments from '../../hooks/useFetchLatePayments';
import useFetchVendors from '../../hooks/useFetchVendors';

const PaymentAnalysis = ({ onLoaded }) => {
  const { fetchLatePayments, loadingLatePayments, errorLatePayments } = useFetchLatePayments();
  const { fetchVendorData, loadingFetchVendorData } = useFetchVendors();

  const [latePaymentsData, setLatePaymentsData] = useState(null);
  const [hoveredPeriod, setHoveredPeriod] = useState(null);
  const [latestPeriod, setLatestPeriod] = useState(null);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [subsidiaryOptions, setSubsidiaryOptions] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [termsFilter, setTermsFilter] = useState('');
  const [loaded, setLoaded] = useState({
    line: false,
    pie: false,
    bar: false,
    status: false,
  });

  const markLoaded = (key) => {
    setLoaded((prev) => {
      if (prev[key]) return prev;
      const updated = { ...prev, [key]: true };
      if (Object.values(updated).every(Boolean)) {
        setTimeout(() => onLoaded?.(), 0);
      }
      return updated;
    });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchLatePayments();
        setLatePaymentsData(data);

        const vendorRes = await fetchVendorData();
        const vendorNames = vendorRes.map((v) => v.companyName).filter(Boolean);
        setVendorOptions([...new Set(vendorNames)]);

        const subsidiaries = data.map((r) => r.subsidiary).filter(Boolean);
        setSubsidiaryOptions([...new Set(subsidiaries)]);
      } catch (err) {
        console.error('Error loading payment analysis data:', err);
      }
    };
    loadData();
  }, []);

  const termsOptions = useMemo(() => {
    const terms = new Set();
    latePaymentsData?.forEach((item) => item.terms && terms.add(item.terms));
    return [...terms].sort();
  }, [latePaymentsData]);

  const filteredData = useMemo(() => {
    if (!latePaymentsData) return [];

    return latePaymentsData.filter((x) => {
      const paymentDate = x.paymentDate ? new Date(x.paymentDate) : null;

      const isStartDateOk = startDate ? paymentDate && paymentDate >= new Date(startDate) : true;
      const isEndDateOk = endDate ? paymentDate && paymentDate <= new Date(endDate) : true;
      const matchesVendor = selectedVendors.length === 0 || selectedVendors.includes(x.name);
      const matchesSubsidiary = selectedSubsidiaries.length === 0 || selectedSubsidiaries.includes(x.subsidiary);
      const matchesTerms = termsFilter ? x.terms === termsFilter : true;

      return isStartDateOk && isEndDateOk && matchesVendor && matchesSubsidiary && matchesTerms;
    });
  }, [latePaymentsData, startDate, endDate, selectedVendors, selectedSubsidiaries, termsFilter]);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedVendors([]);
    setSelectedSubsidiaries([]);
    setTermsFilter('');
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />

        <Autocomplete
          multiple
          size="small"
          options={vendorOptions}
          value={selectedVendors}
          onChange={(e, newValue) => setSelectedVendors(newValue)}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label="Vendors" placeholder="Select Vendors" size="small" />
          )}
          sx={{ minWidth: 300, height: 40 }}
        />

        <Autocomplete
          multiple
          size="small"
          options={subsidiaryOptions}
          value={selectedSubsidiaries}
          onChange={(e, newValue) => setSelectedSubsidiaries(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Subsidiaries"
              placeholder="Select Subsidiaries"
              size="small"
            />
          )}
          sx={{ minWidth: 300, height: 40 }}
        />

        <FormControl size="small" sx={{ minWidth: 180, height: 40 }}>
          <InputLabel>Terms</InputLabel>
          <Select label="Terms" value={termsFilter} onChange={(e) => setTermsFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {termsOptions.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={handleClearFilters} size="small" sx={{ height: 40 }}>
          Clear Filters
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ padding: 2, height: '100%' }}>
            <LatePaymentLineGraoh
              data={filteredData}
              loading={loadingLatePayments}
              error={errorLatePayments}
              onLoaded={() => markLoaded('line')}
              setHoveredPeriod={setHoveredPeriod}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ padding: 2, height: '100%' }}>
            <LatePaymentsPieChart
              data={filteredData}
              loading={loadingLatePayments}
              error={errorLatePayments}
              onLoaded={() => markLoaded('pie')}
              hoveredPeriod={hoveredPeriod}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ padding: 2, height: '100%' }}>
            <LatePaymentsBarChart
              data={filteredData}
              loading={loadingLatePayments}
              error={errorLatePayments}
              onLoaded={() => markLoaded('bar')}
              setHoveredPeriod={setHoveredPeriod}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ padding: 2, height: '100%' }}>
            <ProcessingStatusLineChart
              data={filteredData}
              loading={loadingLatePayments}
              error={errorLatePayments}
              hoveredPeriod={hoveredPeriod}
              setLatestPeriod={setLatestPeriod}
              onLoaded={() => markLoaded('status')}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentAnalysis;
